import { ApiClientV1 } from '../api'
import {
  HorizonContext,
  HostContextValue,
  RegistryContextValue,
} from '../context'
import { EventBus } from '../events'
import { allowedCallbackKeys, Callbacks } from '../globals'
import {
  ComponentLoader,
  ContextRegistry,
  PackageLoader,
  PageBlockRegistry,
} from '../registry'

const isValidRegistryContext = (
  registry: unknown | RegistryContextValue,
  onInvalid?: () => void,
): registry is RegistryContextValue => {
  if (!registry || typeof registry !== 'object') return false

  const isValid =
    'contexts' in registry &&
    'packages' in registry &&
    'components' in registry &&
    'pageBlocks' in registry &&
    registry.contexts instanceof ContextRegistry &&
    registry.packages instanceof PackageLoader &&
    registry.components instanceof ComponentLoader &&
    registry.pageBlocks instanceof PageBlockRegistry

  if (!isValid) onInvalid?.()

  return isValid
}

const isValidCallbacksContext = (
  callbacks: unknown | Callbacks,
  onInvalid?: () => void,
): callbacks is Callbacks => {
  if (!callbacks || typeof callbacks !== 'object') return false

  const isValid = allowedCallbackKeys.every(
    (key) =>
      key in callbacks && typeof (callbacks as Callbacks)[key] === 'function',
  )

  if (!isValid) onInvalid?.()

  return isValid
}

const isValidHostContext = (
  host: unknown | HostContextValue,
  onInvalid?: () => void,
): host is HostContextValue => {
  if (!host || typeof host !== 'object') return false

  const isValid =
    'globalEventBus' in host &&
    'callbacks' in host &&
    host.globalEventBus instanceof EventBus &&
    isValidCallbacksContext(host.callbacks)

  if (!isValid) onInvalid?.()

  return isValid
}

const isValidApiClientContext = (
  apiClient: unknown | ApiClientV1,
  onInvalid?: () => void,
): apiClient is ApiClientV1 => {
  if (apiClient === undefined) return false

  const isValid = apiClient instanceof ApiClientV1 || apiClient === null

  if (!isValid) onInvalid?.()

  return isValid
}

const isValidEventBusContext = (
  eventBus: unknown | EventBus<unknown>,
  onInvalid?: () => void,
): eventBus is EventBus<unknown> => {
  if (!eventBus) return false

  const isValid = eventBus instanceof EventBus

  if (!isValid) onInvalid?.()

  return isValid
}

const invalidContextWarning = (contextKey: keyof HorizonContext) => () => {
  console.warn(
    `Invalid ${contextKey} passed to HorizonProvider. The existing value for this Context will be used instead.`,
  )
}

const contextValidators = {
  apiClient: isValidApiClientContext,
  eventBus: isValidEventBusContext,
  host: isValidHostContext,
  registry: isValidRegistryContext,
}

export const validateAndMergeContext = (
  currentContext: HorizonContext,
  newContext: Partial<HorizonContext> = {},
) => {
  const changedContextKeys = Object.keys(newContext) as (keyof HorizonContext)[]

  return changedContextKeys.reduce<HorizonContext>(
    (updatedContext, contextKey) => {
      const newValue = newContext[contextKey]

      const isValid = contextValidators[contextKey](
        newValue,
        invalidContextWarning(contextKey),
      )

      return {
        ...updatedContext,
        [contextKey]: isValid ? newValue : updatedContext[contextKey],
      }
    },
    currentContext,
  )
}
